/**
 * Environment variable names, judged by name alone: which ones a launched session may never
 * get from Orgus (a leased secret or a settings layer), and which ones hold secrets.
 */

/**
 * Names a launched session may not get from policy or a secret: they change how Orgus, the
 * machine, a shell or Claude Code itself runs, where it sends traffic, which certificates it
 * trusts, or which model it uses (the model is policy's). Compared case-insensitively, since
 * Windows treats `SystemRoot` and `SYSTEMROOT` as one variable.
 */
export const RESERVED_ENV_NAME = new RegExp(
  '^(' +
    [
      // Orgus and Claude Code
      'ORGUS_.*',
      'FLIGHTDECK_.*',
      'CLAUDE_CONFIG_DIR',
      'CLAUDE_CODE_USE_[A-Z]+',
      'CLAUDE_CODE_SKIP_[A-Z]+_AUTH',
      'ANTHROPIC_(BASE_URL|BEDROCK_BASE_URL|VERTEX_BASE_URL|VERTEX_PROJECT_ID|MODEL|SMALL_FAST_MODEL|DEFAULT_[A-Z]+_MODEL)',
      // Where Copilot CLI reads standing instructions: Orgus sets it per launch for the org's standards (ADR 0050).
      'COPILOT_CUSTOM_INSTRUCTIONS_DIRS',
      // The process and the machine
      'PATH',
      'PATHEXT',
      'HOME',
      'USERPROFILE',
      'APPDATA',
      'LOCALAPPDATA',
      'SYSTEMROOT',
      'SYSTEMDRIVE',
      'WINDIR',
      'COMSPEC',
      'TEMP',
      'TMP',
      'TMPDIR',
      'SHELL',
      'BASH_ENV',
      'ENV',
      'ZDOTDIR',
      'LD_.*',
      'DYLD_.*',
      'NODE_.*',
      // Traffic and trust
      '.*_PROXY',
      'SSL_CERT_(FILE|DIR)',
      'REQUESTS_CA_BUNDLE',
      'CURL_CA_BUNDLE',
      // git running programs
      'GIT_SSH',
      'GIT_SSH_COMMAND',
      'GIT_EXEC_PATH',
      'GIT_CONFIG_.*',
      'GIT_ASKPASS',
      'SSH_ASKPASS',
    ].join('|') +
    ')$',
  'i',
);

/** True when a launched session may not get this variable from policy or a secret. */
export function isReservedEnvName(name: string): boolean {
  return RESERVED_ENV_NAME.test(name);
}

/**
 * Names whose values are treated as secrets ("redact env values"). Words are matched whole
 * between underscores, so AUTHOR, KEYBOARD, PASSTHROUGH or MAX_OUTPUT_TOKENS are not secrets,
 * and NPM_AUTHTOKEN, PGPASSWORD or npm's `_authToken` still are.
 */
export const SECRET_ENV_NAME =
  /(^|_)([A-Z]*(TOKEN|SECRETS?|PASSWORDS?|PASSWD)|PASS|PRIVATE|CREDENTIALS?|API_?KEYS?|ACCESS_?KEYS?|AUTH)($|_)|(^|_)KEYS?$/i;

/**
 * Names that match SECRET_ENV_NAME but hold ordinary configuration: a git identity, a
 * credential helper or store, an agent socket, and ids, paths and kinds.
 */
export const NOT_SECRET_ENV_NAME =
  /^(GIT_(AUTHOR|COMMITTER)_.*|SSH_AUTH_SOCK)$|_(STORE|HELPER|SOCK|SOCKET|PATH|FILE|DIR|TYPE|MODE|METHOD|PROVIDER|URL|HOST|PORT|USER|USERNAME|NAME|EMAIL|ID|REGION|DISABLED|ENABLED)$/i;

/** True when this variable's value should be masked wherever it appears. */
export function isSecretEnvName(name: string): boolean {
  return SECRET_ENV_NAME.test(name) && !NOT_SECRET_ENV_NAME.test(name);
}
